import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { ChartLine, CheckDouble, Feed, Hub } from '@factorialco/f0-react/icons/app'
import {
  MySurveysDataCollectionDocument,
  MyTrainingsDataCollectionDocument,
  MyTrainingsDataCollectionFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useTrainingsDatatable } from 'modules/trainings/hooks/useTrainingsDatatable'
import { DEFAULT_DATA_COLLECTION_STORAGE } from 'modules/trainings/lib/collections'

import emptyStateImage from './Empty-state-catalog-trainings-card.png'
import { KnowledgeTestMembership, calculateTrainingProgressPercentage } from './getTrainingProgress'

export const MyTrainingCardsView = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { access, employee } = useCurrentSession()

  const [myCourses, setMyCourses] = useState<MyTrainingsDataCollectionFragment[]>([])
  const {
    getModalityTranslation,
    getDataCollectionTrainingAttendanceColor,
    getTrainingAttendanceTranslation,
  } = useTrainingsDatatable()

  //Query knowledge tests for all trainings
  const { data: knowledgeTestsData } = useQuery(MySurveysDataCollectionDocument, {
    variables: {
      formType: ['knowledge'],
      respondentId: String(access?.id),
    },
  })

  // Build a Mapping between each trainingId and knowledge test membership data
  const knowledgeTestsDataByTrainingId = useMemo(() => {
    const map = new Map<string, KnowledgeTestMembership[]>()
    const memberships = knowledgeTestsData?.forms?.membershipsConnection?.nodes ?? []
    memberships.forEach((membership) => {
      const trainingId = membership.materializedForm?.form?.resourceId
      if (trainingId) {
        map.set(trainingId, [
          ...(map.get(trainingId) ?? []),
          {
            respondentId: membership.respondentId ?? null,
            status: membership.status ?? undefined,
            score: membership.score ?? null,
          },
        ])
      }
    })
    return map
  }, [knowledgeTestsData])

  const getMandatoryCount = useCallback(
    (groupId: boolean | undefined) => {
      return myCourses.length
        ? myCourses.filter((node) =>
            String(groupId) === 'true' ? node.isMandatory : !node.isMandatory
          ).length
        : 0
    },
    [myCourses]
  )

  const source = useGraphqlDataCollectionSource({
    document: MyTrainingsDataCollectionDocument,
    mapData: (data) => {
      const nodes = data?.trainings?.trainingsConnection?.nodes ?? []
      setMyCourses(nodes)
      return nodes
    },
    mapPagination: (data) => {
      return {
        total: data?.trainings?.trainingsConnection?.totalCount ?? 0,
      }
    },
    mapVariables: ({ filters, search, sortings }) => {
      const filterObject: Record<string, unknown> = {}

      if (filters.status && filters.status.length > 0) {
        filterObject.trainingAttendanceStatus = { in: filters.status }
      }

      if (filters.creationYear && filters.creationYear.length > 0) {
        const year = Number(filters.creationYear[0])
        if (!isNaN(year)) {
          const yearStart = `${year}-01-01`
          const yearEnd = `${year}-12-31`
          filterObject.createdAt = { between: [yearStart, yearEnd] }
        }
      }
      return {
        accessId: access?.id,
        employeeId: employee?.id,
        search,
        filter: Object.keys(filterObject).length > 0 ? filterObject : undefined,
        status: 'active',
        sortOrder: sortings?.[0],
      }
    },
    filters: {
      status: {
        label: i18n.t('trainings.my_trainings_revamp.overview.courses_datatable.status_column'),
        type: 'in',
        options: {
          cache: true,
          options: async () => [
            {
              label: i18n.t('trainings.my_training.all_courses_table.not_started'),
              value: 'notstarted',
            },
            {
              label: i18n.t('trainings.my_training.all_courses_table.started'),
              value: 'started',
            },
            {
              label: i18n.t('trainings.my_training.all_courses_table.completed'),
              value: 'completed',
            },
            {
              label: i18n.t('trainings.my_training.all_courses_table.partially_completed'),
              value: 'partiallycompleted',
            },
            {
              label: i18n.t('trainings.my_training.all_courses_table.absent'),
              value: 'missing',
            },
          ],
        },
      },
      creationYear: {
        label: i18n.t('trainings.create_training_modal.year'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const currentYear = new Date().getFullYear()
            const years = []
            for (let i = currentYear + 1; i >= currentYear - 25; i--) {
              years.push({
                label: String(i),
                value: String(i),
              })
            }
            return years
          },
        },
      },
    },
    search: {
      enabled: true,
    },
    sortings: {
      isMandatory: {
        label: i18n.t(
          'trainings.my_trainings_revamp.overview.courses_datatable.group_by.mandatory'
        ),
      },
    },
    defaultSortings: {
      field: 'isMandatory',
      order: 'desc',
    },
    itemActions: (item: MyTrainingsDataCollectionFragment) => [
      {
        label: i18n.t('trainings.my_trainings_revamp.overview.courses_datatable.course_page'),
        type: 'primary',
        onClick: () => {
          navigate(
            navigation.myTrainings.revamp.detailsRevamp.view.overview.fullPath({
              trainingId: item.id,
            })
          )
        },
      },
    ],

    grouping: {
      collapsible: true,
      defaultOpenGroups: true,
      mandatory: true,
      groupBy: {
        isMandatory: {
          name: i18n.t(
            'trainings.my_trainings_revamp.overview.courses_datatable.group_by.mandatory'
          ),
          defaultDirection: 'asc' as const,
          label(groupId) {
            return String(groupId) === 'true'
              ? i18n.t(
                  'trainings.my_trainings_revamp.overview.courses_datatable.group_by.mandatory_learning'
                )
              : i18n.t(
                  'trainings.my_trainings_revamp.overview.courses_datatable.group_by.optional_learning'
                )
          },
          itemCount: (groupId) => getMandatoryCount(groupId),
        },
      },
    },
  })

  return (
    <OneDataCollection
      id="trainings/my_trainings_cards_view/v1"
      storage={DEFAULT_DATA_COLLECTION_STORAGE}
      source={source}
      emptyStates={{
        'no-data': {
          title: i18n.t(
            'trainings.my_trainings_revamp.overview.courses_datatable.empty_state.title'
          ),
          description: i18n.t(
            'trainings.my_trainings_revamp.overview.courses_datatable.empty_state.description'
          ),
        },
      }}
      visualizations={[
        {
          type: 'card',
          options: {
            title: (item) => item.name,
            image: (item) => item.thumbnail ?? emptyStateImage,
            cardProperties: [
              {
                label: '',
                icon: CheckDouble,
                render: (item: MyTrainingsDataCollectionFragment) => {
                  const isKnowledgeTestRequired =
                    item?.completionConfiguration?.knowledgeTestRequired
                  const trainingKnowledgeTests = knowledgeTestsDataByTrainingId.get(String(item.id))

                  return {
                    type: 'text',
                    value: isKnowledgeTestRequired
                      ? calculateTrainingProgressPercentage(item, trainingKnowledgeTests) + '%'
                      : calculateTrainingProgressPercentage(item) + '%',
                  }
                },
              },
              {
                label: '',
                icon: ChartLine,
                render: (item: MyTrainingsDataCollectionFragment) => {
                  const isKnowledgeTestRequired =
                    item?.completionConfiguration?.knowledgeTestRequired
                  const trainingKnowledgeTests = knowledgeTestsDataByTrainingId.get(String(item.id))
                  return {
                    type: 'progressBar',
                    value: {
                      label: '%',
                      value: isKnowledgeTestRequired
                        ? calculateTrainingProgressPercentage(item, trainingKnowledgeTests)
                        : calculateTrainingProgressPercentage(item),
                    },
                  }
                },
              },
              {
                label: '',
                icon: Feed,
                render: (item: MyTrainingsDataCollectionFragment) => ({
                  type: 'status' as const,
                  value: {
                    status: getDataCollectionTrainingAttendanceColor(
                      item?.trainingAttendanceStatus
                    ),
                    label: item.trainingAttendanceStatus
                      ? getTrainingAttendanceTranslation(item?.trainingAttendanceStatus)
                      : '',
                  },
                }),
              },
              {
                label: '',
                icon: Hub,
                render: (item: MyTrainingsDataCollectionFragment) => ({
                  type: 'text',
                  value: getModalityTranslation(item.trainingModality),
                }),
              },
            ],
          },
        },
      ]}
    />
  )
}
